import { FiInfo } from "react-icons/fi"

import { Title, Container, DurationInput, Select } from "components/Form"
import useT from "utils/hooks/useT"

// ReviewSpecification — edits the Review Specification of an Analysis definition (PRD-003 Area 1
// / SPEC-007): which competence group(s) may perform the second-person review and how long the
// review takes. It lives OUTSIDE the analysis's Task Design flow — a review is not a procedure
// step and must NOT count toward the analysis Minimum Duration.
//
// It is a controlled component: the parent (AnalysisConfigurationPage) owns the
// `allowedGroupIds` (string[]) and `duration` (a Luxon Duration | null) state and persists them
// via the limsReviewSpec slice on save (keyed by the analysis-definition id).
//
// Both inputs are optional. When no competence group is chosen, any member may review; when no
// duration is given, the review falls back to two hours — both resolved at review-creation time
// (see limsReviews.ensureForAnalysis) so the review is always IAP-schedulable.
//
// Layout (PRD-006 K19): the section heading lives INSIDE the box; the two fields are stacked
// (competence groups, then duration); the helper text comes LAST, presented with the shared
// LIMS-config info-icon convention (FiInfo + visible text). The shared box/heading/field/help
// classes (`.LimsConfigBox-*`) are defined once in index.scss and reused by SystemSuitability.
export default function ReviewSpecification({ competenceGroups, allowedGroupIds, onAllowedGroupIdsChange, duration, onDurationChange }) {
  const { t } = useT("common")

  return (
    <div className="container-compartment AnalysisConfigurationPage-lims-compartment LimsConfigBox">
      <h4 className="LimsConfigBox-heading">{t("reviewSpecification")}</h4>

      <div className="LimsConfigBox-fields">
        <Container>
          <Title width="14rem">{t("reviewSpecGroups")}</Title>
          <Select
            name={t("reviewSpecGroups")}
            collection={Object.values(competenceGroups).sort((a, b) => {
              if (a.name.toUpperCase() < b.name.toUpperCase()) return -1
              if (a.name.toUpperCase() > b.name.toUpperCase()) return 1
              return 0
            })}
            value={allowedGroupIds}
            onChange={onAllowedGroupIdsChange}
            menuPosition="fixed"
          />
        </Container>
        <Container>
          <Title width="14rem">{t("reviewSpecDuration")}</Title>
          <DurationInput value={duration} onChange={onDurationChange} />
        </Container>
      </div>

      <p className="LimsConfigBox-help">
        <FiInfo className="LimsConfigBox-help-icon" title={t("reviewSpecGroupsHelp")} />
        <span>{t("reviewSpecGroupsHelp")}</span>
      </p>
    </div>
  )
}
